var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });

// schemas/env.ts
import { z } from "zod";
var RawApiEnvSchema = z.object({
  // Required on purpose: it controls cookie security flags and the dev OTP
  // bypass, so a missing or misspelled value must fail at boot, never
  // silently fall back to development.
  APP_ENV: z.enum(["development", "production"]),
  PORT: z.coerce.number().default(3e3),
  HOST: z.string().default("0.0.0.0"),
  APP_URL: z.url().optional(),
  // App version for cache-busting / forcing client refresh on deploy
  APP_VERSION: z.string().optional(),
  MONGODB_URI: z.string().min(1),
  CORS_ORIGIN: z.string().default("http://localhost:5173"),
  // JWT secret (support both names)
  JWT_SECRET: z.string().min(32).optional(),
  APP_SECRET_KEY: z.string().min(32).optional(),
  // Twilio (support both Verify service vars)
  TWILIO_ACCOUNT_SID: z.string().regex(/^AC[A-Za-z0-9]{32}$/, "TWILIO_ACCOUNT_SID must start with AC"),
  TWILIO_SERVICE_SID: z.string().regex(/^VA[A-Za-z0-9]{32}$/, "TWILIO_SERVICE_SID must start with VA"),
  TWILIO_AUTH_TOKEN: z.string().min(32),
  // Optional override for the SMS sender. Either an E.164 phone number or a
  // Messaging Service SID (MG...). When unset, the API discovers the account's
  // single SMS-capable number at runtime.
  TWILIO_SMS_FROM: z.string().regex(/^(\+[1-9]\d{6,14}|MG[A-Za-z0-9]{32})$/, "TWILIO_SMS_FROM must be E.164 or an MG... SID").optional(),
  // Optional integrations
  RECAPTCHA_V3_SECRET: z.string().optional(),
  // URL used to verify reCAPTCHA tokens (Google's v3 verify endpoint by default)
  RECAPTCHA_VERIFY_URL: z.url().optional(),
  // Backblaze
  BACKBLAZE_KEY_ID: z.string(),
  BACKBLAZE_KEY_NAME: z.string(),
  BACKBLAZE_APPLICATION_KEY: z.string(),
  BACKBLAZE_BUCKET_ID: z.string(),
  BACKBLAZE_DOWNLOAD_URL: z.url(),
  HUBSPOT_CLIENT_TOKEN: z.string(),
  // Private app client secret: used by HubSpot to sign inbound webhooks
  // (X-HubSpot-Signature-v3). Distinct from the access token above.
  HUBSPOT_CLIENT_SECRET: z.string().min(1).optional(),
  // Google OAuth (optional)
  GOOGLE_CLIENT_ID: z.string().optional(),
  GOOGLE_CLIENT_SECRET: z.string().optional(),
  GOOGLE_CALLBACK_URL: z.string().optional(),
  SENTRY_AUTH_TOKEN: z.string(),
  // Sentry runtime/build variables (optional)
  SENTRY_DSN: z.string().optional(),
  SENTRY_ENV: z.string().optional(),
  SENTRY_RELEASE: z.string().optional(),
  SENTRY_TRACES_SAMPLE_RATE: z.string().optional(),
  SENTRY_CAPTURE_DEV: z.string().optional(),
  SENTRY_ORG: z.string().optional(),
  SENTRY_PROJECT: z.string().optional()
}).superRefine((v, ctx) => {
  if (!v.JWT_SECRET && !v.APP_SECRET_KEY) {
    ctx.addIssue({ code: "custom", message: "Provide JWT_SECRET or APP_SECRET_KEY" });
  }
});
var WebEnvSchema = z.object({
  VITE_GTM_ID: z.string().optional(),
  VITE_API_URL: z.url(),
  VITE_PUBLIC_APP_URL: z.url().optional(),
  VITE_APP_ENV: z.string().optional(),
  VITE_RECAPTCHA_V3_KEY: z.string().optional(),
  VITE_HUBSPOT_CLIENT_TOKEN: z.string().optional(),
  VITE_HUBSPOT_REDIRECT_ENABLED: z.string().optional(),
  VITE_HUBSPOT_REDIRECT_URL: z.url().optional(),
  VITE_TUITION_THIRD_LABEL: z.string().optional(),
  VITE_TUITION_THIRD_URL: z.string().optional(),
  VITE_TUITION_ONLINE_SOLD_OUT: z.enum(["true", "false"]).optional(),
  VITE_TUITION_SF_SOLD_OUT: z.enum(["true", "false"]).optional(),
  VITE_TUITION_NEXT_YEAR_SOLD_OUT: z.enum(["true", "false"]).optional(),
  VITE_TUITION_SHOW_THIRD: z.enum(["true", "false"]).optional(),
  // Optional Sentry client keys
  VITE_SENTRY_DSN: z.string().optional(),
  VITE_SENTRY_ENV: z.string().optional(),
  VITE_SENTRY_RELEASE: z.string().optional(),
  VITE_SENTRY_TRACES_SAMPLE_RATE: z.string().optional()
});
function parseWebEnv(input) {
  return WebEnvSchema.parse(input);
}
__name(parseWebEnv, "parseWebEnv");
function parseApiEnv(input) {
  const raw = RawApiEnvSchema.parse(input);
  const defaultVersion = raw.APP_ENV === "development" ? "dev" : `v${Date.now()}`;
  return {
    APP_ENV: raw.APP_ENV,
    PORT: raw.PORT,
    HOST: raw.HOST,
    APP_URL: raw.APP_URL ?? `http://localhost:${raw.PORT}`,
    APP_VERSION: raw.APP_VERSION ?? defaultVersion,
    MONGODB_URI: raw.MONGODB_URI,
    CORS_ORIGIN: raw.CORS_ORIGIN,
    JWT_SECRET: raw.JWT_SECRET ?? raw.APP_SECRET_KEY,
    TWILIO_ACCOUNT_SID: raw.TWILIO_ACCOUNT_SID,
    TWILIO_AUTH_TOKEN: raw.TWILIO_AUTH_TOKEN,
    TWILIO_SERVICE_SID: raw.TWILIO_SERVICE_SID,
    TWILIO_SMS_FROM: raw.TWILIO_SMS_FROM,
    RECAPTCHA_V3_SECRET: raw.RECAPTCHA_V3_SECRET,
    RECAPTCHA_VERIFY_URL: raw.RECAPTCHA_VERIFY_URL,
    BACKBLAZE_KEY_ID: raw.BACKBLAZE_KEY_ID,
    BACKBLAZE_KEY_NAME: raw.BACKBLAZE_KEY_NAME,
    BACKBLAZE_APPLICATION_KEY: raw.BACKBLAZE_APPLICATION_KEY,
    BACKBLAZE_BUCKET_ID: raw.BACKBLAZE_BUCKET_ID,
    BACKBLAZE_DOWNLOAD_URL: raw.BACKBLAZE_DOWNLOAD_URL,
    HUBSPOT_CLIENT_TOKEN: raw.HUBSPOT_CLIENT_TOKEN,
    HUBSPOT_CLIENT_SECRET: raw.HUBSPOT_CLIENT_SECRET,
    GOOGLE_CLIENT_ID: raw.GOOGLE_CLIENT_ID,
    GOOGLE_CLIENT_SECRET: raw.GOOGLE_CLIENT_SECRET,
    GOOGLE_CALLBACK_URL: raw.GOOGLE_CALLBACK_URL,
    SENTRY_AUTH_TOKEN: raw.SENTRY_AUTH_TOKEN,
    SENTRY_DSN: raw.SENTRY_DSN,
    SENTRY_ENV: raw.SENTRY_ENV,
    SENTRY_RELEASE: raw.SENTRY_RELEASE,
    SENTRY_TRACES_SAMPLE_RATE: raw.SENTRY_TRACES_SAMPLE_RATE,
    SENTRY_CAPTURE_DEV: raw.SENTRY_CAPTURE_DEV,
    SENTRY_ORG: raw.SENTRY_ORG,
    SENTRY_PROJECT: raw.SENTRY_PROJECT
  };
}
__name(parseApiEnv, "parseApiEnv");
export {
  WebEnvSchema,
  parseApiEnv,
  parseWebEnv
};
